import { inject, Injectable } from "@angular/core";
import { ExerciseDto } from "@clients/squidex/dtos/exercise.dto";
import { AudioExercise, DropDownExercise, Exercise, ExerciseId } from "@domain/Exercise";
import { HomophoneGroup } from "@domain/HomophoneGroup";
import { SQUIDEX_CLIENT } from "@squidexClient";

const AUDIO_EXERCISE_SCHEMA_NAME = "audio-exercise";
const DROP_DOWN_EXERCISE_SCHEMA_NAME = "drop-down-exercise";

export interface GetRandomExerciseOptions {
  homophoneGroups: HomophoneGroup[];
  excludedExerciseIds?: ExerciseId[];
  includedExerciseIds?: ExerciseId[];
}

@Injectable({
  providedIn: "root",
})
export class ExerciseClient {
  private client = inject(SQUIDEX_CLIENT);

  async getRandomExercise(options: GetRandomExerciseOptions): Promise<Exercise | undefined> {
    const queryParams = this.getRandomExerciseQueryParams(options);
    const data = await this.client.contents.getContents("exercises", { q: JSON.stringify(queryParams) });

    const exerciseDto = data?.items?.[0];
    if (!exerciseDto) return;

    const homophoneGroup = options.homophoneGroups.find(homophoneGroup => homophoneGroup.id === exerciseDto.data.homophoneGroup.iv[0]);
    if (!homophoneGroup) throw new Error("Invalid exercise without homophoneGroup");

    return this.mapDtoToDomain(exerciseDto, homophoneGroup);
  }

  private getRandomExerciseQueryParams(options?: GetRandomExerciseOptions) {
    const basicFilters = { random: 1 };

    if (!options) return basicFilters;

    const homophoneGroups = options.homophoneGroups.length > 0 ? options.homophoneGroups : undefined;
    const excludedExerciseIds = (options.excludedExerciseIds && options.excludedExerciseIds.length > 0 && options.excludedExerciseIds) || undefined;
    const includedExerciseIds = (options.includedExerciseIds && options.includedExerciseIds.length > 0 && options.includedExerciseIds) || undefined;

    if (!homophoneGroups && !excludedExerciseIds && !includedExerciseIds) {
      return basicFilters;
    }

    const homophoneGroupFilters =
      (homophoneGroups && {
        or: homophoneGroups.map((homophoneGroup: HomophoneGroup) => ({
          path: "data/homophoneGroup/iv",
          op: "eq",
          value: homophoneGroup.id,
        })),
      }) ||
      undefined;
    if (!excludedExerciseIds && !includedExerciseIds) {
      return {
        ...basicFilters,
        filter: homophoneGroupFilters,
      };
    }

    const includedExerciseIdsFilter =
      (includedExerciseIds && {
        or: includedExerciseIds.map(exerciseId => ({
          path: "id",
          op: "eq",
          value: exerciseId.toString(),
        })),
      }) ||
      undefined;

    if (includedExerciseIdsFilter && !homophoneGroups && !excludedExerciseIds) {
      return {
        ...basicFilters,
        filter: includedExerciseIdsFilter,
      };
    }

    const excludedExerciseIdsFilters =
      (excludedExerciseIds &&
        excludedExerciseIds.map(exerciseId => ({
          path: "id",
          op: "ne",
          value: exerciseId.toString(),
        }))) ??
      undefined;

    return {
      ...basicFilters,
      filter: {
        and: [
          ...(homophoneGroupFilters ? [homophoneGroupFilters] : []),
          ...(excludedExerciseIdsFilters ?? []),
          ...(includedExerciseIdsFilter ? [includedExerciseIdsFilter] : []),
        ],
      },
    };
  }

  private mapDtoToDomain(exerciseDto: ExerciseDto, homophoneGroup: HomophoneGroup): Exercise | undefined {
    const exerciseId = new ExerciseId(exerciseDto.id);

    const data = exerciseDto.data.exercise.iv;
    const { correctAnswer, sentence, positiveFeedback, constructiveFeedback } = exerciseDto.data.exercise.iv;

    if (data.schemaName === AUDIO_EXERCISE_SCHEMA_NAME) {
      return new AudioExercise({
        id: exerciseId,
        sentence: sentence,
        correctAnswer,
        positiveFeedback,
        constructiveFeedback,
        audioFileName: homophoneGroup.audioFileName,
      });
    }

    if (data.schemaName === DROP_DOWN_EXERCISE_SCHEMA_NAME) {
      return new DropDownExercise({
        id: exerciseId,
        sentence: sentence,
        correctAnswer,
        positiveFeedback,
        constructiveFeedback,
        suggestedAnswers: homophoneGroup.suggestedAnswers,
      });
    }

    throw new Error("Invalid exercise type");
  }
}
